import { useState } from "react";
import apiService from "services/api";
import { Collapse } from "reactstrap";
import { useHistory, Link } from "react-router-dom";
import { useNotificationContext } from "contexts";
import { useForm, useFieldArray } from "react-hook-form";
import { Button } from "reactstrap";
import { FormEntry } from "components";
import { courseSchema, mapLessonsByCategory, newLessonSchema } from "./constants";
import { NestedFieldArray, HorizontalLine } from "./components";
import { FiX, FiChevronsUp, FiChevronsDown } from "react-icons/fi";
import { capitalize } from "utils/capitalize";

const NewCoursePage = () => {
	const history = useHistory();

	const { register, handleSubmit, reset, control, watch } = useForm({
		defaultValues: courseSchema,
	});

	const {
		fields: lessons,
		append,
		remove,
	} = useFieldArray({
		control,
		name: "lessons",
	});

	const { createNotification } = useNotificationContext();

	const submitNewCourse = (data) => {
		apiService.course
			.create(data)
			.then((response) => {
				createNotification({
					type: "success",
					message: "Successfully created a new course",
				});
				history.push("/course");
			})
			.catch((error) =>
				createNotification({
					type: "danger",
					message: error.message,
				})
			);
	};

	const addAnother = (category) => {
		append(newLessonSchema(category));
	};

	const removeLesson = (idx) => remove(idx);

	const resetFields = () => {
		reset(courseSchema);
	};

	const [openCategories, setOpenCategories] = useState([]);

	const lessonsByCategories = mapLessonsByCategory(lessons);

	const toggleCollapse = (category) => {
		if (openCategories.includes(category)) return setOpenCategories((categories) => categories.filter((cat) => cat !== category));
		return setOpenCategories((categories) => [...categories, category]);
	};

	return (
		<form onSubmit={handleSubmit(submitNewCourse)} className="content">
			<h4 className="text-center text-secondary mb-4 font-weight-bold my-3">Add New Course</h4>

			<div className="d-flex justify-content-center">
				<div className="w-100">
					<div className="mb-5">
						<Link to="/course">
							<button className="btn btn-sm btn-info">&lt; Back</button>
						</Link>
					</div>

					<FormEntry formLabel="Name" placeholder="Course Name" name="name" type="text" register={register} required={true} />
					<FormEntry formLabel="Price" placeholder="Course Price" name="price" type="number" register={register} />

					<h4 className="text-center text-secondary mb-4 mt-5 font-weight-bold">Lessons</h4>

					{lessonsByCategories.map(({ lessons, category }) => (
						<div
							key={category}
							className="col-12 py-2 mb-5"
							style={{
								border: "2px solid #ffffff25",
								borderRadius: 8,
								backgroundColor: "#1D253B",
								cursor: "pointer",
							}}
							id="accordion"
							role="tablist"
						>
							<div
								className="d-flex justify-content-between align-items-center mb-2"
								aria-expanded={openCategories.includes(category)}
								data-parent="#accordion"
								data-toggle="collapse"
								onClick={() => {
									if (lessons.length) toggleCollapse(category);
								}}
							>
								<div className="display-6 font-weight-bold text-white">{capitalize(category)}</div>
								<Button
									className="btn-link edit text-warning d-flex align-items-center border border-warning"
									color="primary"
									size="sm"
									onClick={(e) => {
										if (!openCategories.includes(category)) toggleCollapse(category);
										addAnother(category);
										e.stopPropagation();
									}}
									type="button"
								>
									+ Lesson
								</Button>
							</div>
							<HorizontalLine />
							{lessons.length ? (
								lessons.map((lesson, lessonIndex) => (
									<div key={lesson.id} className="my-1">
										<Collapse isOpen={openCategories.includes(category)} onClick={(e) => e.stopPropagation()}>
											<div className="d-flex justify-content-between align-items-baseline my-2">
												<div className="text-white font-weight-bold ml-2">Lesson {lessonIndex + 1} </div>

												<Button
													color="warning"
													size="sm"
													className="btn-link edit text-danger d-flex align-items-center border border-danger"
													onClick={() => removeLesson(lesson.index)}
													type="button"
												>
													<FiX className="mr-1" /> Lesson
												</Button>
											</div>
											<FormEntry
												type="text"
												formLabel="Name"
												name={`lessons.${lesson.index}.name`}
												placeholder={`Lesson ${lessonIndex + 1} Name`}
												required={true}
												register={register}
											/>

											<FormEntry
												type="text"
												formLabel="Code"
												name={`lessons.${lesson.index}.code`}
												placeholder={`Lesson ${lessonIndex + 1} Code`}
												required={true}
												register={register}
											/>

											<FormEntry
												type="text"
												formLabel="Filepath"
												name={`lessons.${lesson.index}.filepath`}
												placeholder={`Lesson ${lessonIndex + 1} File path`}
												required={true}
												register={register}
											/>
											<FormEntry
												type="text"
												formLabel="Classwork URL"
												name={`lessons.${lesson.index}.classworkURL`}
												placeholder={`Lesson ${lessonIndex + 1} Classwork URL`}
												register={register}
											/>

											<FormEntry
												type="text"
												formLabel="Test & Review URL"
												name={`lessons.${lesson.index}.testAndReviewURL`}
												placeholder={`Lesson ${lessonIndex + 1} Test & Review URL`}
												register={register}
											/>

											<FormEntry
												type="text"
												formLabel="Homework URL"
												name={`lessons.${lesson.index}.homeworkURL`}
												placeholder={`Lesson ${lessonIndex + 1} Homework URL`}
												register={register}
											/>
											<NestedFieldArray
												control={control}
												register={register}
												index={lessonIndex}
												namePrefix={`lessons[${lesson.index}].learningObjectives`}
											/>
											{lessonIndex !== lessons.length - 1 && <HorizontalLine />}
										</Collapse>
									</div>
								))
							) : (
								<div className="text-center text-muted mt-3 mb-1">No Lessons Yet</div>
							)}
							{Boolean(lessons.length) && (
								<div
									className="text-center text-muted mt-3 mb-1"
									onClick={() => {
										if (lessons.length) toggleCollapse(category);
									}}
								>
									{!openCategories.includes(category) ? (
										<FiChevronsDown style={{ fontSize: 28 }} />
									) : (
										<FiChevronsUp style={{ fontSize: 28 }} />
									)}
								</div>
							)}
						</div>
					))}

					<div className="d-flex justify-content-end mt-5">
						<button className="btn btn-success mx-1" type="submit">
							Submit
						</button>
						<button className="btn btn-secondary" type="button" onClick={resetFields}>
							Reset
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default NewCoursePage;
